<template>
  <div class="grid grid-rows-[auto_1fr] overflow-hidden">
    <div class="bg-avtalat-teal py-lg text-white">
      <p
        className="type-heading-md flex flex-row gap-sm items-center justify-center"
      >
        <font-awesome-icon
          class="text-white"
          icon="fa-regular fa-circle-info"
          size="lg"
        />
        <span class="anstalld-header__heading-name">Releaseinformation</span>
      </p>
    </div>
    <ContainerGrid scrollable>
      <SidebarColumn>
        <div id="release-accordion" class="px-lg py-sm pb-lg" >
          <avtalat-accordion v-for="(year, index) in releaseYears" :key="year">
            <avtalat-accordion-item
              :heading="year"
              headingStyle="accordion__toggle"
              :open="index ? false : true"
            >
              <ul class="release-menu">
                <li
                  :id="'release-' + release.fullName"
                  class="hover:bg-[#F0F0F0] active:bg-[#E8E8E8]"
                  v-for="(release, index) in releaseStore.getReleasesForYear(
                    year,
                  )"
                  :key="index"
                >
                  <router-link :to="`/releaser/${release.fullName}`"
                    >{{ release.fullName }}
                  </router-link>
                </li>
              </ul>
            </avtalat-accordion-item>
          </avtalat-accordion>
        </div>
      </SidebarColumn>
      <MainColumn>
        <div
          id="release-content" class="custom-scrollbar col-span-2 mt-md gap-3xl overflow-y-scroll rounded-md bg-white p-lg"
        >
          <avtalat-spinner v-if="releaseStore.isLoading" />
          <template v-else>
            <h1 class="release-content__heading">
              {{ releaseStore.currentRelease?.fullName }}
            </h1>
            <div
              class="release-content__description"
              v-if="releaseStore.currentRelease?.content"
              v-html="compiledMarkdown(releaseStore.currentRelease?.content)"
            ></div>
          </template>
        </div>
      </MainColumn>
    </ContainerGrid>
  </div>
</template>
<script setup lang="ts">
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { marked } from "marked";
import { computed, onMounted, watch } from "vue";

import { ContainerGrid, MainColumn, SidebarColumn } from "@/layout";
import { useNotificationsStore } from "@/layout/app-header/notifications-menu";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";

import { useReleaseStore } from "./store";

const props = defineProps({
  fullName: {
    type: String,
  },
});

library.add(faCircleInfo);
const releaseStore = useReleaseStore();
const notificationsStore = useNotificationsStore();
const apiClient = useApi();
const navigation = useNavigation();

const releaseYears = computed(() => {
  return releaseStore.getReleaseYears();
});

watch(
  () => [props.fullName],
  async ([fullName]) => {
    if (!fullName) return;
    if (releaseStore.releases.length === 0) {
      await releaseStore.fetchReleases(apiClient);
    }
    const release = await releaseStore.fetchRelease(fullName, apiClient);
    if (release) notificationsStore.markAsSeen(release.id);
  },
  { immediate: true },
);

onMounted(async () => {
  await releaseStore.fetchReleases(apiClient);
  if (!props.fullName) {
    const latestRelease = releaseStore.getLatestRelease();
    if (latestRelease) {
      navigation.openRelease(latestRelease.fullName);
    } else {
      navigation.router.back();
    }
  }
});

const compiledMarkdown = (input: string) => {
  if (!input) return;
  return marked(input);
};
</script>
<style scoped>
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4),
:deep(h5),
:deep(h6) {
  font-weight: revert;
  font-size: revert;
}

.release-menu {
  padding: 0;
  list-style: none;
}

.release-menu li a {
  display: block;
  border-radius: 5px;
  padding: 10px 0 10px 20px;
  width: 100%;
  line-height: 30px;
  text-decoration: none;
}

.release-menu li a.router-link-exact-active {
  background-color: #142547;
  color: #fff;
}

.release-content__heading {
  margin-bottom: 20px;
  font-weight: bold;
  font-size: 24px;
}

.release-content__description :deep(ul) {
  margin: 1em 0;
  padding-left: 2em;
  list-style-type: disc;
}
.release-content__description :deep(ol) {
  margin: 1em 0;
  padding-left: 02em;
  list-style-type: decimal;
}

.release-content__description :deep(p) {
  margin: 1em 0;
}
</style>
<style>
.sidebar__left {
  border-right: 1px solid var(--neutral-200);
  width: 360px;
}

.sidebar__left details > summary {
  padding-right: 24px;
}
</style>
